// @ts-nocheck
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { getRlTeams, type RlTeam } from "@/lib/rlTeams";
import { goToRecruitLook } from "@/lib/recruitlookHandoff";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CalendarDays, MapPin, Users } from "lucide-react";
import { toast } from "sonner";
import { formatCalendarDate, formatCalendarRange } from "@/lib/dates";

const sb = supabase as any;
const SUPABASE_URL = "https://kvttsygfutwcywroraji.supabase.co";
const SUPABASE_ANON_KEY =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imt2dHRzeWdmdXR3Y3l3cm9yYWppIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzA4MTU1OTUsImV4cCI6MjA4NjM5MTU5NX0.wUaZcZxKQpyk2DTzTF2CjYHvWRHa897xW0rleO6mJos";


type InviteError = { code: string; message: string; invitee_email?: string };

function errorFor(code: string, payload: any): InviteError {
  switch (code) {
    case "NOT_FOUND":
      return { code, message: "We couldn't find this invitation. Ask the organiser to resend it." };
    case "EXPIRED": {
      const when = payload?.expired_at
        ? formatCalendarDate(String(payload.expired_at).slice(0, 10))
        : null;
      return {
        code,
        message: `This invitation expired${when ? ` on ${when}` : ""}. Ask the organiser to resend it.`,
      };
    }
    case "REVOKED":
      return { code, message: "This invitation was cancelled. Ask the organiser to resend it." };
    case "ALREADY_ACCEPTED":
      return {
        code,
        message: "You've already accepted this invitation.",
        invitee_email: payload?.invitee_email,
      };
    case "NO_TOKEN":
    default:
      return { code: "NO_TOKEN", message: "This link is malformed. Ask the organiser to resend the invitation." };
  }
}

export default function CoachInviteAccept() {
  const { token } = useParams<{ token: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState<any>(null);
  const [error, setError] = useState<InviteError | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState<InviteError | null>(null);
  const [team, setTeam] = useState<RlTeam | null>(null);

  // Inline account creation (this app has no public sign-up route)
  const [signupOpen, setSignupOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [signupLoading, setSignupLoading] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);


  const autoAcceptTried = useRef(false);

  const invitePath = `/coach-invite/${token}`;
  // Returning from sign-in or email confirmation should finish the acceptance
  // without making the coach press the button a second time.
  const returnUrl = `${invitePath}?accept=1`;

  useEffect(() => {
    (async () => {
      if (!token) {
        setError(errorFor("NO_TOKEN"));
        setLoading(false);
        return;
      }
      const { data, error: rpcError } = await sb.rpc("rle_get_team_invitation", { p_token: token });
      if (rpcError) {
        setError({ code: "NOT_FOUND", message: rpcError.message });
      } else if (!data?.ok) {
        setError(errorFor(data?.code, data));
      } else {
        setInvite(data);
        // Resolve the team name through RecruitLook in the background — a slow
        // or failed lookup must never block acceptance.
        if (data.team_id) {
          getRlTeams([String(data.team_id)])
            .then((teams) => {
              const t = teams[String(data.team_id)];
              if (t) setTeam(t);
            })
            .catch(() => {});
        }
      }
      setLoading(false);
    })();
  }, [token]);

  const goToTeamRoster = async (teamId: string | null | undefined) => {
    // A coach's home is RecruitLook, not RecruitLook Events. Hand them off
    // signed-in to their team's roster, or to the coach dashboard if no team id.
    const next = teamId ? `/club-coach-dashboard/teams/${teamId}/roster` : undefined;
    await goToRecruitLook(next);
  };

  const accept = async () => {
    if (!invite || !user) return;
    setAccepting(true);
    setAcceptError(null);
    const { data, error: rpcError } = await sb.rpc("rle_accept_team_invitation", { p_token: token });
    setAccepting(false);

    if (rpcError) {
      toast.error(rpcError.message);
      return;
    }
    if (data?.ok && data?.code === "ACCEPTED") {
      toast.success("Welcome! You're now coaching this team.");
      await goToTeamRoster(data.team_id ?? invite.team_id);
      return;
    }
    switch (data?.code) {
      case "EMAIL_MISMATCH":
        setAcceptError({
          code: "EMAIL_MISMATCH",
          message: `This invitation is for ${data.invitee_email} but you're signed in as ${data.signed_in_as}.`,
          invitee_email: data.invitee_email,
        });
        break;
      case "SIGN_IN_REQUIRED":
        navigate(`/auth/login?email=${encodeURIComponent(invite.invitee_email ?? "")}&redirect=${encodeURIComponent(returnUrl)}`);
        break;
      default:
        setError(errorFor(data?.code, data));
    }
  };

  const emailMismatch =
    user && invite?.invitee_email &&
    user.email?.toLowerCase() !== invite.invitee_email.toLowerCase();

  // Auto-accept when the coach lands back here signed in as the invited
  // address (after sign-in redirect or email confirmation).
  useEffect(() => {
    if (
      searchParams.get("accept") === "1" &&
      invite &&
      user &&
      !authLoading &&
      !emailMismatch &&
      !accepting &&
      !autoAcceptTried.current
    ) {
      autoAcceptTried.current = true;
      setSearchParams({}, { replace: true });
      accept();
    }
  }, [searchParams, invite, user, authLoading, emailMismatch, accepting]);

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    if (password.length < 8) {
      setPasswordError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setPasswordError("Passwords don't match.");
      return;
    }
    setSignupLoading(true);
    try {
      // Invitation-gated sign-up: the endpoint creates/reuses a RecruitLook
      // account, mirrors it into Events under the same id, confirms the email,
      // accepts the invitation, and provisions the coach role — all server-side.
      // No confirmation email and no second accept step.
      const res = await fetch(`${SUPABASE_URL}/functions/v1/rle-invite-signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json", apikey: SUPABASE_ANON_KEY },
        body: JSON.stringify({ token, password, name: invite.invitee_name ?? undefined }),
      });
      const out = await res.json().catch(() => null);

      if (!out?.ok) {
        const code = out?.code;
        if (code === "WEAK_PASSWORD") {
          setPasswordError(out?.message || "Password must be at least 8 characters.");
          return;
        }
        if (["NOT_FOUND", "EXPIRED", "REVOKED", "ALREADY_ACCEPTED"].includes(code)) {
          setError(errorFor(code, out));
          return;
        }
        if (code === "ID_MISMATCH") {
          setAcceptError({
            code,
            message: out?.message || "This email already has an Events account that isn't linked to its RecruitLook account. Contact support to merge them.",
          });
          return;
        }
        if (["RL_NOT_CONFIGURED", "RL_LOOKUP_FAILED", "RL_CREATE_FAILED"].includes(code)) {
          toast.error(out?.message || "RecruitLook is unreachable right now. Please try again.");
          return;
        }
        toast.error(out?.message || "Something went wrong. Please try again.");
        return;
      }

      // The password lives on RecruitLook; the Events mirror has none.
      // Open the Events session with the magiclink token the endpoint returned.
      if (out.hashed_token) {
        const { error: verifyError } = await sb.auth.verifyOtp({
          token_hash: out.hashed_token,
          type: "magiclink",
        });
        if (verifyError) {
          // Account already exists and invitation is already accepted; fall back
          // to sign-in with the pre-filled email so their password works.
          navigate(`/auth/login?email=${encodeURIComponent(out.email)}`);
          return;
        }
      } else {
        // Account is set up but we have no magiclink token; send them to sign-in.
        navigate(`/auth/login?email=${encodeURIComponent(out.email)}`);
        return;
      }

      toast.success("Welcome! You're now coaching this team.");
      await goToTeamRoster(out.team_id ?? invite.team_id);
    } finally {
      setSignupLoading(false);
    }
  };


  const switchAccount = async () => {
    await sb.auth.signOut();
    navigate(`/auth/login?email=${encodeURIComponent(invite.invitee_email ?? "")}&redirect=${encodeURIComponent(returnUrl)}`);
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center space-y-3">
            <p className="text-lg font-semibold">{error.message}</p>
            {error.code === "ALREADY_ACCEPTED" && (
              <Button asChild className="w-full">
                <Link
                  to={`/auth/login${error.invitee_email ? `?email=${encodeURIComponent(error.invitee_email)}` : ""}`}
                >
                  Sign in{error.invitee_email ? ` as ${error.invitee_email}` : ""}
                </Link>
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const event = invite.event;
  const teamLabel = team
    ? [team.display_name || team.name, team.age_group].filter(Boolean).join(" · ")
    : null;

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="pt-6">
          <h1 className="text-xl font-bold">Coach Invitation</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {invite.invitee_name ? <><strong>{invite.invitee_name}</strong>, you've</> : "You've"} been
            invited to coach as <strong>{invite.role === "head_coach" ? "head coach" : invite.role}</strong>.
          </p>

          {teamLabel && (
            <p className="mt-3 flex items-center gap-1.5 text-base font-semibold">
              <Users className="w-4 h-4 text-muted-foreground" />
              {teamLabel}
            </p>
          )}

          {event && (
            <div className="mt-3 rounded-lg border p-3 space-y-1.5 text-sm">
              <p className="font-medium">{event.name}</p>
              {(event.start_date || event.end_date) && (
                <p className="flex items-center gap-1.5 text-muted-foreground">
                  <CalendarDays className="w-3.5 h-3.5" />
                  {formatCalendarRange(event.start_date, event.end_date)}
                </p>
              )}
              {(event.venue_name || event.venue_city) && (
                <p className="flex items-center gap-1.5 text-muted-foreground">
                  <MapPin className="w-3.5 h-3.5" />
                  {[event.venue_name, [event.venue_city, event.venue_state].filter(Boolean).join(", ")]
                    .filter(Boolean)
                    .join(" — ")}
                </p>
              )}
            </div>
          )}

          {invite.personal_message && (
            <p className="text-sm mt-3 italic border-l-2 border-primary pl-3">
              "{invite.personal_message}"
            </p>
          )}

          <div className="mt-6 space-y-2">
            {!user ? (
              signupOpen ? (

                <form onSubmit={handleSignup} className="space-y-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="invite-email">Email</Label>
                    <Input id="invite-email" type="email" value={invite.invitee_email} readOnly disabled />
                    <p className="text-xs text-muted-foreground">
                      This invitation is for this address only.
                    </p>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="invite-password">Create a password</Label>
                    <Input
                      id="invite-password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      minLength={8}
                      required
                      autoComplete="new-password"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="invite-password-confirm">Confirm password</Label>
                    <Input
                      id="invite-password-confirm"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      minLength={8}
                      required
                      autoComplete="new-password"
                    />
                  </div>
                  {passwordError && (
                    <p className="text-sm text-destructive">{passwordError}</p>
                  )}

                  <Button type="submit" className="w-full" disabled={signupLoading}>
                    {signupLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Create account & accept
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full"
                    onClick={() => setSignupOpen(false)}
                  >
                    Back
                  </Button>
                </form>
              ) : (
                <>
                  <p className="text-sm">
                    Sign in or sign up as <strong>{invite.invitee_email}</strong> to accept.
                  </p>
                  <Button
                    className="w-full"
                    onClick={() =>
                      navigate(`/auth/login?email=${encodeURIComponent(invite.invitee_email ?? "")}&redirect=${encodeURIComponent(returnUrl)}`)
                    }
                  >
                    Sign in to accept
                  </Button>
                  <Button variant="outline" className="w-full" onClick={() => setSignupOpen(true)}>
                    Sign up to accept
                  </Button>
                </>
              )
            ) : emailMismatch || acceptError?.code === "EMAIL_MISMATCH" ? (
              <>
                <p className="text-sm text-amber-700">
                  {acceptError?.code === "EMAIL_MISMATCH"
                    ? acceptError.message
                    : <>This invitation is for <strong>{invite.invitee_email}</strong>. You're signed in as <strong>{user.email}</strong>.</>}
                </p>
                <Button variant="outline" className="w-full" onClick={switchAccount}>
                  Sign in with the right account
                </Button>
              </>
            ) : (
              <Button className="w-full" onClick={accept} disabled={accepting}>
                {accepting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Accept invitation
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
